import { match } from 'ts-pattern';
import type { MediaType } from '~/graphql';

// MIME types
// - https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/MIME_types/Common_types
// - https://www.iana.org/assignments/media-types/media-types.xhtml

// allowed file types "source of truth":
// - https://github.com/wistia/granary/blob/main/internal/pkg/mediaapi/mediatypes.go#L17
// - https://support.wistia.com/en/articles/8268259-export-settings#h_f0483feacf

// allowed file extensions for different media types
const videoFileExtensions = [
  '.mov',
  '.mpg',
  '.avi',
  '.flv',
  '.f4v',
  '.mp4',
  '.m4v',
  '.asf',
  '.wmv',
  '.vob',
  '.mod',
  '.3gp',
  '.mkv',
  '.divx',
  '.xvid',
  '.webm',
  '.mxf',
];
const audioFileExtensions = ['.mp3', '.wav', '.aif', '.au', '.m4a', '.ogg'];
const imageFileExtensions = ['.jpg', '.jpeg', '.png', '.tiff', '.bmp', '.dcm'];
const fontFileExtensions = ['.woff', '.woff2', '.ttf', '.otf'];
const pdfFileExtensions = ['.pdf'];
const csvFileExtensions = ['.csv'];
const microsoftOfficeDocumentFileExtensions = ['.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx'];

// MIME types - https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/MIME_types/Common_types
const videoMimeType = 'video/*';
const audioMimeType = 'audio/*';
const imageMimeType = 'image/*';
const fontMimeType = 'font/*';
const pdfMimeType = 'application/pdf';
const csvMimeType = 'text/csv';
const microsoftOfficeWordMimeType = 'application/msword';
const microsoftOfficeWordOpenXmlMimeType =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const microsoftOfficePowerPointMimeType = 'application/vnd.ms-powerpoint';
const microsoftOfficePowerPointOpenXmlMimeType =
  'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const microsoftOfficeExcelMimeType = 'application/vnd.ms-excel';
const microsoftOfficeExcelOpenXmlMimeType =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const acceptVideoFiles = {
  [videoMimeType]: videoFileExtensions,
};

export const acceptAudioFiles = {
  [audioMimeType]: audioFileExtensions,
};

export const acceptImageFiles = {
  [imageMimeType]: imageFileExtensions,
};

export const acceptFontFiles = {
  [fontMimeType]: fontFileExtensions,
};

export const acceptPDFFiles = {
  [pdfMimeType]: pdfFileExtensions,
};

export const acceptCSVFiles = {
  [csvMimeType]: csvFileExtensions,
};

export const acceptMicrosoftOfficeFiles = {
  [microsoftOfficeWordMimeType]: [microsoftOfficeDocumentFileExtensions[0]], // .doc
  [microsoftOfficeWordOpenXmlMimeType]: [microsoftOfficeDocumentFileExtensions[1]], // .docx
  [microsoftOfficePowerPointMimeType]: [microsoftOfficeDocumentFileExtensions[2]], // .ppt
  [microsoftOfficePowerPointOpenXmlMimeType]: [microsoftOfficeDocumentFileExtensions[3]], // .pptx
  [microsoftOfficeExcelMimeType]: [microsoftOfficeDocumentFileExtensions[4]], // .xls
  [microsoftOfficeExcelOpenXmlMimeType]: [microsoftOfficeDocumentFileExtensions[5]], // .xlsx
};

export const acceptAudioVideoFiles = {
  ...acceptAudioFiles,
  ...acceptVideoFiles,
};

export const acceptAllFiles = {
  ...acceptVideoFiles,
  ...acceptAudioFiles,
  ...acceptImageFiles,
  ...acceptPDFFiles,
  ...acceptMicrosoftOfficeFiles,
};

const acceptedDocumentFileExtensions = [
  ...pdfFileExtensions,
  ...microsoftOfficeDocumentFileExtensions,
];

const acceptedAudioAndVideoFileExtensions = [...audioFileExtensions, ...videoFileExtensions];

const allAcceptedFileExtensions = [
  ...videoFileExtensions,
  ...audioFileExtensions,
  ...imageFileExtensions,
  ...acceptedDocumentFileExtensions,
];

export const acceptedAllFileExtensions = [...allAcceptedFileExtensions].join(', ');

export const acceptedAudioVideoFileExtensions = [...acceptedAudioAndVideoFileExtensions].join(', ');

export const acceptedImageFileExtensions = [...imageFileExtensions].join(', ');

export const acceptedFontFileExtensions = [...fontFileExtensions].join(', ');

export const formattedAcceptedFileExtensionsByCurrentMediaType = (
  currentMediaType: MediaType,
): string =>
  match(currentMediaType)
    .with('Video', () => videoFileExtensions)
    .with('Audio', () => audioFileExtensions)
    .with('Image', () => imageFileExtensions)
    .with('PdfDocument', () => pdfFileExtensions)
    .with('MicrosoftOfficeDocument', () => microsoftOfficeDocumentFileExtensions)
    .with('Document', () => acceptedDocumentFileExtensions)
    .otherwise(() => [])
    .join(', ');
